import React, { useState } from 'react';
import { Send, Sparkles, Download, Copy, Check, MessageSquare, Settings } from 'lucide-react';

const WizardBuilder = () => {
  const [messages, setMessages] = useState([
    {
      role: 'assistant',
      content: 'Hi! I can help you build a custom quotation wizard. Tell me about your service or product, and I\'ll create a tailored wizard with the right steps and fields.\n\nFor example, you could say:\n- "I need a wizard for car rental bookings"\n- "Create a wizard for event planning services"\n- "I want to collect customer info for equipment hire"'
    }
  ]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [generatedWizard, setGeneratedWizard] = useState(null);
  const [copied, setCopied] = useState(false);

  // Mock LLM call - replace with actual API call later
  const callLLM = async (userMessage, conversationHistory) => {
    // Simulate API delay
    await new Promise(resolve => setTimeout(resolve, 1500));
    
    // Mock response based on user input
    if (userMessage.toLowerCase().includes('car rental') || userMessage.toLowerCase().includes('vehicle')) {
      return {
        response: "Great! I'll create a car rental quotation wizard for you. This will include:\n\n1. **Vehicle Selection** - Choose car type and features\n2. **Rental Period** - Pick-up and drop-off dates\n3. **Driver Information** - Contact and license details\n4. **Location Details** - Pick-up/drop-off locations\n5. **Additional Services** - Insurance, GPS, child seats, etc.\n\nWould you like to:\n- Add or remove any steps?\n- Include specific vehicle categories?\n- Add custom fields for your business needs?",
        wizard: {
          wizardSteps: [
            {
              id: "vehicleSelection",
              label: "Vehicle Selection",
              description: "Choose your preferred vehicle type and features",
              fields: [
                {
                  id: "vehicleType",
                  label: "Vehicle Type",
                  dataType: "RadioSelection",
                  isRequired: true,
                  options: [
                    { id: "economy", label: "Economy", image: "" },
                    { id: "sedan", label: "Sedan", image: "" },
                    { id: "suv", label: "SUV", image: "" },
                    { id: "luxury", label: "Luxury", image: "" }
                  ]
                },
                {
                  id: "transmissionType",
                  label: "Transmission",
                  dataType: "RadioSelection",
                  isRequired: true,
                  options: [
                    { id: "automatic", label: "Automatic" },
                    { id: "manual", label: "Manual" }
                  ]
                }
              ]
            },
            {
              id: "rentalPeriod",
              label: "Rental Period",
              description: "Specify your rental dates and duration",
              fields: [
                {
                  id: "pickupDate",
                  label: "Pick-up Date",
                  dataType: "Date",
                  isRequired: true
                },
                {
                  id: "dropoffDate",
                  label: "Drop-off Date",
                  dataType: "Date",
                  isRequired: true
                }
              ]
            },
            {
              id: "driverInfo",
              label: "Driver Information",
              description: "Provide driver details and contact information",
              fields: [
                {
                  id: "fullName",
                  label: "Full Name",
                  dataType: "Text",
                  isRequired: true,
                  maxCharacters: "200"
                },
                {
                  id: "email",
                  label: "Email",
                  dataType: "Email",
                  isRequired: true
                },
                {
                  id: "mobile",
                  label: "Mobile Number",
                  dataType: "PhoneNumber",
                  isRequired: true
                },
                {
                  id: "licenseNumber",
                  label: "Driver's License Number",
                  dataType: "Text",
                  isRequired: true
                }
              ]
            }
          ]
        }
      };
    }
    
    // Default response for other queries
    return {
      response: "I understand you need a quotation wizard. To create the perfect wizard for you, could you provide more details?\n\n- What service or product are you offering?\n- What information do you need to collect from customers?\n- Are there any specific steps or requirements you have in mind?\n\nThe more details you provide, the better I can customize the wizard for your needs!",
      wizard: null
    };
  };

  const handleSend = async () => {
    if (!input.trim()) return;

    const userMessage = {
      role: 'user',
      content: input
    };

    setMessages(prev => [...prev, userMessage]);
    setInput('');
    setIsLoading(true);

    try {
      // In production, replace this with actual API call
      const response = await callLLM(input, messages);
      
      const assistantMessage = {
        role: 'assistant',
        content: response.response
      };

      setMessages(prev => [...prev, assistantMessage]);
      
      if (response.wizard) {
        setGeneratedWizard(response.wizard);
      }
    } catch (error) {
      const errorMessage = {
        role: 'assistant',
        content: 'Sorry, I encountered an error. Please try again.'
      };
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleCopyJSON = () => {
    if (generatedWizard) {
      navigator.clipboard.writeText(JSON.stringify(generatedWizard, null, 2));
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const handleDownloadJSON = () => {
    if (generatedWizard) {
      const blob = new Blob([JSON.stringify(generatedWizard, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'wizard-config.json';
      a.click();
      URL.revokeObjectURL(url);
    }
  };

  return (
    <div className="flex h-screen bg-gray-50">
      {/* Chat Section */}
      <div className="flex-1 flex flex-col">
        {/* Header */}
        <div className="bg-white border-b border-gray-200 px-6 py-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-purple-600 rounded-lg flex items-center justify-center">
              <Sparkles className="w-5 h-5 text-white" />
            </div>
            <div>
              <h1 className="text-xl font-bold text-gray-900">Wizard Builder AI</h1>
              <p className="text-sm text-gray-500">Describe your needs, get a custom wizard</p>
            </div>
          </div>
        </div>

        {/* Messages */}
        <div className="flex-1 overflow-y-auto px-6 py-4 space-y-4">
          {messages.map((msg, idx) => (
            <div
              key={idx}
              className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
            >
              <div
                className={`max-w-2xl rounded-2xl px-4 py-3 ${
                  msg.role === 'user'
                    ? 'bg-blue-600 text-white'
                    : 'bg-white border border-gray-200 text-gray-900'
                }`}
              >
                <div className="flex items-start gap-2">
                  {msg.role === 'assistant' && (
                    <MessageSquare className="w-5 h-5 mt-0.5 flex-shrink-0 text-blue-600" />
                  )}
                  <p className="text-sm whitespace-pre-wrap leading-relaxed">{msg.content}</p>
                </div>
              </div>
            </div>
          ))}
          
          {isLoading && (
            <div className="flex justify-start">
              <div className="bg-white border border-gray-200 rounded-2xl px-4 py-3">
                <div className="flex items-center gap-2">
                  <div className="flex gap-1">
                    <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></div>
                    <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></div>
                    <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></div>
                  </div>
                  <span className="text-sm text-gray-500">Generating wizard...</span>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Input */}
        <div className="border-t border-gray-200 bg-white px-6 py-4">
          <div className="flex gap-3">
            <input
              type="text"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && handleSend()}
              placeholder="Describe your quotation wizard needs..."
              className="flex-1 px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              disabled={isLoading}
            />
            <button
              onClick={handleSend}
              disabled={isLoading || !input.trim()}
              className="px-6 py-3 bg-blue-600 text-white rounded-xl hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center gap-2"
            >
              <Send className="w-4 h-4" />
              Send
            </button>
          </div>
        </div>
      </div>

      {/* JSON Preview Section */}
      <div className="w-96 border-l border-gray-200 bg-white flex flex-col">
        <div className="px-6 py-4 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Settings className="w-5 h-5 text-gray-600" />
              <h2 className="font-semibold text-gray-900">Generated Config</h2>
            </div>
            {generatedWizard && (
              <div className="flex gap-2">
                <button
                  onClick={handleCopyJSON}
                  className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                  title="Copy JSON"
                >
                  {copied ? (
                    <Check className="w-4 h-4 text-green-600" />
                  ) : (
                    <Copy className="w-4 h-4 text-gray-600" />
                  )}
                </button>
                <button
                  onClick={handleDownloadJSON}
                  className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                  title="Download JSON"
                >
                  <Download className="w-4 h-4 text-gray-600" />
                </button>
              </div>
            )}
          </div>
        </div>

        <div className="flex-1 overflow-y-auto p-6">
          {generatedWizard ? (
            <div className="bg-gray-900 rounded-lg p-4 overflow-x-auto">
              <pre className="text-xs text-green-400 font-mono">
                {JSON.stringify(generatedWizard, null, 2)}
              </pre>
            </div>
          ) : (
            <div className="flex items-center justify-center h-full text-center">
              <div className="text-gray-400">
                <Settings className="w-12 h-12 mx-auto mb-3 opacity-50" />
                <p className="text-sm">No wizard generated yet</p>
                <p className="text-xs mt-1">Start a conversation to generate your wizard config</p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default WizardBuilder;